import React from "react";
import { Link as ReactRouterLink } from "react-router-dom";
import type { LinkProps } from "react-router-dom";
import { getPageLanguage } from "@kivra/sdk/copy-consumer";
import { getFocusStyle } from "../../theme";
import type { ColorToken } from "../../theme/styled";
import { isPropValid, styled } from "../../theme/styled";
import type { KivraCSSObject } from "../../theme/styled/styled";
import {
  type ButtonProps,
  getStyles as getButtonVariantStyles,
} from "../buttons/Button";
import { BaseStyles as getButtonBaseStyles } from "../buttons/Button.styles";
import { OpenExternallyIcon } from "../icons/new-icons";
import { font } from "../typography/baseStyling";
import { VisuallyHidden } from "../util/VisuallyHidden";

export interface Props extends Omit<LinkProps, "href"> {
  /**
   * Optionally set the color of the link.
   * Defaults to `$green-400`.
   */
  color?: ColorToken;
  /**
   * Content of the Link.
   */
  children?: React.ReactNode;
  /**
   * Hides the icon.
   */
  hideIcon?: boolean;
  /**
   * If you would like to style the Link as Button
   */
  buttonStyles?: {
    variant: ButtonProps["variant"];
    size: ButtonProps["size"];
  };
}

export const isExternal = (to: string): boolean =>
  to.startsWith("http://") ||
  to.startsWith("https://") ||
  to.startsWith("mailto:") ||
  to.startsWith("tel:");

export const Link = (props: Props): React.JSX.Element => {
  const { to, color = "$green-400", children, buttonStyles, ...rest } = props;

  const mappedChildren = React.Children.map(
    children,
    (child: React.ReactNode) => {
      if (typeof child === "string") {
        return <Text>{child}</Text>;
      }
      return child;
    },
  );

  if (typeof to === "string" && isExternal(to)) {
    const language = getPageLanguage();
    const ariaTextMap = new Map<string | null, string | null>([
      ["en", ", opens in a new tab"],
      ["sv", ", öppnas i ny flik"],
    ]);
    const ariaText = ariaTextMap.get(language);

    return (
      <NativeAnchorStyled
        {...rest}
        href={to}
        rel={rest.rel ?? "noreferrer noopener"}
        target={rest.target ?? "_blank"}
        color={color}
        buttonStyles={buttonStyles}
        data-component-type="Link"
      >
        {children && !props.hideIcon && (
          <ExternalLinkIcon
            size={buttonStyles?.size === "small" ? 16 : 24}
            _color="currentColor"
            aria-hidden
          />
        )}
        {buttonStyles ? children : mappedChildren}
        {ariaText && <VisuallyHidden as="span">{ariaText}</VisuallyHidden>}
      </NativeAnchorStyled>
    );
  }

  return (
    <ReactRouterLinkStyled
      {...rest}
      to={to}
      rel={rest.rel ?? "noreferrer noopener"}
      color={color}
      buttonStyles={buttonStyles}
      data-component-type="Link"
    >
      {buttonStyles ? children : mappedChildren}
    </ReactRouterLinkStyled>
  );
};

const baseStyle = (color?: ColorToken): KivraCSSObject => {
  return {
    ...getFocusStyle(),
    textDecoration: "none",
    color: color,

    "&:hover": {
      textDecoration: "none",
    },
  };
};

const Text = styled("span")({
  textDecoration: "underline",
  textUnderlinePosition: "under",
  textDecorationThickness: "1px",
  ...font.BodyM,

  "&:hover": {
    textDecorationThickness: "2px",
  },
});

const getButtonStyling = (
  buttonStyles: Props["buttonStyles"],
): KivraCSSObject => {
  return {
    ...getButtonBaseStyles({
      iconOnly: false,
      size: buttonStyles?.size,
      children: undefined,
    }),
    ...getButtonVariantStyles(buttonStyles?.variant),
  };
};

const ReactRouterLinkStyled = styled(ReactRouterLink, {
  forwardProp: (prop) => isPropValid(prop) && prop !== "color",
})<Pick<Props, "color" | "buttonStyles">>(({ color, buttonStyles }) => ({
  ...(buttonStyles ? getButtonStyling(buttonStyles) : baseStyle(color)),
}));

const NativeAnchorStyled = styled("a", {
  forwardProp: (prop) => isPropValid(prop) && prop !== "color",
})<Pick<Props, "color" | "buttonStyles">>(({ color, buttonStyles }) => ({
  ...(buttonStyles ? getButtonStyling(buttonStyles) : baseStyle(color)),
}));

const ExternalLinkIcon = styled(OpenExternallyIcon)({
  marginRight: "$spacing-10",
  verticalAlign: "middle",
});

Link.Text = Text;
